import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users, Plus, Search, Compass, UserCheck } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { usePageRefresh } from '../../contexts/RefreshContext';
import { useSquads } from '../hooks/useSquads';
import { sportTypeAPI } from '../../services/api';
import { squadInvitationService } from '../services/squadInvitationService';
import { btn } from '../../utils/buttonStyles';
import SquadCard from '../components/SquadCard';
import SquadCreateEdit from '../components/SquadCreateEdit';
import Tabs from '../../components/common/Tabs';

/**
 * SquadListPage - Squads Listing Page
 *
 * Route: /squad/ and /squads
 *
 * Features:
 * - Two tabs: "Discover Squads" and "My Squads"
 * - Search and sport filter
 * - Create new squad modal
 * - Join/Leave squad actions
 * - Accept invitations
 */

const SquadListPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, authChecked } = useAuth();
  const { setBreadcrumbItems } = useBreadcrumb();

  // Pull-to-refresh (mobile app has no address-bar reload)
  usePageRefresh(() => window.location.reload());

  // Get saved tab preference from localStorage
  const getSavedTab = () => localStorage.getItem('squadListPageTab') || 'discover';

  const [activeTab, setActiveTab] = useState(getSavedTab());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSportFilter, setSelectedSportFilter] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sportTypes, setSportTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [serverSearched, setServerSearched] = useState(false);

  const { groups, loading, error, fetchGroups, fetchUserGroups, joinGroup, leaveGroup } = useSquads(false);

  // Track last fetch time to prevent redundant refetches on visibility change
  const lastFetchedAt = React.useRef(0);

  // Save tab preference and reset pagination
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
    setSelectedSportFilter('All');
    setServerSearched(false);
    localStorage.setItem('squadListPageTab', tab);
  };

  // Client-side filter on already-loaded groups
  const filteredGroups = groups.filter(squad => {
    const matchesSearch = !searchTerm || (
      squad.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      squad.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesSport = selectedSportFilter === 'All' || squad.sportTypeId === parseInt(selectedSportFilter);
    return matchesSearch && matchesSport;
  });

  const showServerHint = searchTerm.length > 0 && !serverSearched;

  // Handle search on Enter key (only if 4+ characters)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.length >= 4) {
      setCurrentPage(1);
      handleSearch();
    }
  };

  // Perform server-side search
  const handleSearch = async () => {
    const filters = {
      search: searchTerm.length >= 4 ? searchTerm : '',
      sportTypeId: selectedSportFilter !== 'All' ? selectedSportFilter : '',
      page: 1,
      limit: 10
    };

    if (activeTab === 'discover') {
      const result = await fetchGroups(filters, false);
      setHasMoreResults(result?.pagination?.hasMore || false);
    } else {
      const result = await fetchUserGroups(filters, false);
      setHasMoreResults(result?.pagination?.hasMore || false);
    }
    setServerSearched(true);
    setCurrentPage(1);
  };

  // Handle clear filters — full server reset
  const handleClearFilters = async () => {
    setSearchTerm('');
    setSelectedSportFilter('All');
    setCurrentPage(1);
    setServerSearched(false);

    const filters = { page: 1, limit: 10 };
    if (activeTab === 'discover') {
      const result = await fetchGroups(filters, false);
      setHasMoreResults(result?.pagination?.hasMore || false);
    } else {
      const result = await fetchUserGroups(filters, false);
      setHasMoreResults(result?.pagination?.hasMore || false);
    }
  };

  // Handle load more — always carries current search + sport filter
  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    const filters = {
      search: searchTerm.length >= 4 ? searchTerm : '',
      sportTypeId: selectedSportFilter !== 'All' ? selectedSportFilter : '',
      page: nextPage,
      limit: 10
    };

    if (activeTab === 'discover') {
      const result = await fetchGroups(filters, true);
      setHasMoreResults(result?.pagination?.hasMore || false);
    } else {
      const result = await fetchUserGroups(filters, true);
      setHasMoreResults(result?.pagination?.hasMore || false);
    }

    setCurrentPage(nextPage);
  };

  // Redirect to signin if not authenticated
  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      navigate('/signin', { replace: true });
    }
  }, [authChecked, isAuthenticated, navigate]);

  // Fetch sport types for filtering
  useEffect(() => {
    const loadSportTypes = async () => {
      try {
        const response = await sportTypeAPI.getAll();
        setSportTypes(response?.data || response || []);
      } catch (error) {
        console.error('Failed to load sport types:', error);
      }
    };
    loadSportTypes();
  }, []);

  // Set breadcrumb
  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'Squads' }
    ]);
    return () => {
      setBreadcrumbItems([]);
    };
  }, [setBreadcrumbItems]);

  // Fetch groups based on active tab - initial load
  useEffect(() => {
    if (!authChecked || !isAuthenticated) return;

    const filters = { page: 1, limit: 10 };
    const fetch = activeTab === 'discover' ? fetchGroups : fetchUserGroups;
    fetch(filters, false).then(result => {
      lastFetchedAt.current = Date.now();
      setHasMoreResults(result?.pagination?.hasMore || false);
    });
  }, [authChecked, isAuthenticated, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh groups when returning to this page — but only if data is older than 30s
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && authChecked && isAuthenticated) {
        if (Date.now() - lastFetchedAt.current < 30000) return;
        const filters = { page: 1, limit: 10 };
        const fetch = activeTab === 'discover' ? fetchGroups : fetchUserGroups;
        fetch(filters, false).then(result => {
          lastFetchedAt.current = Date.now();
          setHasMoreResults(result?.pagination?.hasMore || false);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authChecked, isAuthenticated, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch from server whenever sport filter changes
  useEffect(() => {
    if (!authChecked || !isAuthenticated) return;
    const filters = {
      search: searchTerm.length >= 4 ? searchTerm : '',
      sportTypeId: selectedSportFilter !== 'All' ? selectedSportFilter : '',
      page: 1,
      limit: 10,
    };
    const fetchFn = activeTab === 'discover' ? fetchGroups : fetchUserGroups;
    fetchFn(filters, false).then(result => {
      setCurrentPage(1);
      setHasMoreResults(result?.pagination?.hasMore || false);
    });
  }, [selectedSportFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle squad creation success — always switch to My Squads tab
  // because the Discover tab excludes groups the user is already a member of
  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setActiveTab('myGroups');
    localStorage.setItem('squadListPageTab', 'myGroups');
    setSearchTerm('');
    setSelectedSportFilter('All');
    setCurrentPage(1);
    setServerSearched(false);
    fetchUserGroups({ page: 1, limit: 10 });
  };

  // Join squad handler
  const handleJoinGroup = async (squad) => {
    const result = await joinGroup(squad.id);
    if (result.success) {
      toast.success(result.pending ? 'Join request sent! Waiting for admin approval.' : 'Successfully joined squad!');
    } else {
      toast.error(`Failed to join squad: ${result.error}`);
    }
  };

  // Leave squad handler
  const handleLeaveGroup = async (squad) => {
    const result = await leaveGroup(squad.id);
    if (result.success) {
      toast.success('Successfully left squad!');
      // Refresh the list
      activeTab === 'discover' ? fetchGroups() : fetchUserGroups();
    } else {
      toast.error(`Failed to leave squad: ${result.error}`);
    }
  };

  // Accept invitation handler
  const handleAcceptInvitation = async (squad) => {
    const result = await squadInvitationService.acceptInvitation(squad.id);
    if (result.success) {
      toast.success('You have successfully joined the squad!');
      activeTab === 'discover' ? fetchGroups() : fetchUserGroups();
    } else {
      toast.error(`Failed to accept invitation: ${result.error}`);
    }
  };

  if (!authChecked || !isAuthenticated) return null;

  return (
    <div className={`${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Tabs + Create in one row */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1">
            <Tabs
              tabs={[
                { id: 'discover', label: 'Discover Squads', shortLabel: 'Discover', icon: Compass },
                { id: 'myGroups', label: 'My Squads', shortLabel: 'My Squads', icon: UserCheck }
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showMobileMenu={false}
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1.5 shrink-0`}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create</span>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search squads (min 4 characters, press Enter)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className={`w-full pl-10 pr-4 py-3 border ${theme.border} rounded-xl focus:ring-2 ${theme.link.replace('text-', 'focus:ring-')} focus:border-transparent ${theme.input}`}
            />
          </div>
          <div className="flex gap-2">
            <select
              value={selectedSportFilter}
              onChange={(e) => setSelectedSportFilter(e.target.value)}
              className={`px-4 py-3 border ${theme.border} rounded-xl focus:ring-2 ${theme.link.replace('text-', 'focus:ring-')} focus:border-transparent ${theme.input}`}
            >
              <option value="All">All Sports</option>
              {sportTypes.map(sport => (
                <option key={sport.id} value={sport.id}>{sport.sportName}</option>
              ))}
            </select>
            {(searchTerm || selectedSportFilter !== 'All') && (
              <button
                onClick={handleClearFilters}
                className={`${btn.base} ${btn.md} ${theme.buttonGhost}`}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Server search hint / active search badge */}
        {serverSearched && searchTerm.length >= 4 ? (
          <div className={`flex items-center justify-between text-sm ${theme.textSecondary} mb-4`}>
            <span>Showing server results for <strong className={theme.text}>"{searchTerm}"</strong></span>
            <button
              onClick={handleClearFilters}
              className={`text-sm font-medium ${theme.link} hover:underline ml-4`}
            >
              Clear search
            </button>
          </div>
        ) : showServerHint ? (
          <p className={`text-sm ${theme.textSecondary} mb-4`}>
            Showing matches from loaded results. Press <kbd className="px-1.5 py-0.5 rounded border text-xs font-mono">Enter</kbd> to search all squads on the server.
          </p>
        ) : null}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className={`w-8 h-8 border-4 ${theme.link.replace('text-', 'border-')} border-t-transparent rounded-full animate-spin`}></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Error loading squads: {error.message}</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className={`text-xl font-semibold ${theme.text} mb-2`}>
              {(searchTerm || selectedSportFilter !== 'All')
                ? 'No squads found'
                : activeTab === 'discover'
                  ? 'No squads found'
                  : 'You haven\'t joined any squads yet'}
            </h3>
            <p className={`${theme.textSecondary} mb-6`}>
              {(searchTerm || selectedSportFilter !== 'All')
                ? 'Try adjusting your search or filter criteria'
                : activeTab === 'discover'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Explore and join squads that match your interests'}
            </p>
            {activeTab === 'myGroups' && !searchTerm && selectedSportFilter === 'All' && (
              <button
                onClick={() => handleTabChange('discover')}
                className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
              >
                Discover Squads
              </button>
            )}
            {(searchTerm || selectedSportFilter !== 'All') && (
              <button
                onClick={handleClearFilters}
                className={`${btn.base} ${btn.lg} ${theme.buttonGhost} ml-2`}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filteredGroups.map(squad => (
                <SquadCard
                  key={squad.id}
                  squad={squad}
                  sportTypes={sportTypes}
                  onJoin={handleJoinGroup}
                  onLeave={handleLeaveGroup}
                  onAcceptInvitation={handleAcceptInvitation}
                />
              ))}
            </div>

            {/* Load More — only shown when not actively client-filtering */}
            {hasMoreResults && searchTerm.length < 4 && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Create Squad Modal */}
        <SquadCreateEdit
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          mode="create"
        />
      </div>
    </div>
  );
};

export default SquadListPage;
